import React, { useMemo } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { useAnalyticsContext } from '../analytics-context';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const COUNTRY_CANONICAL: Record<string, string> = {
  'united states of america': 'united states',
  'united states': 'united states',
  usa: 'united states',
  'united kingdom of great britain and northern ireland': 'united kingdom',
  'united kingdom': 'united kingdom',
  uk: 'united kingdom',
  'russian federation': 'russia',
  russia: 'russia',
  'korea republic of': 'south korea',
  'korea, republic of': 'south korea',
  'south korea': 'south korea',
  'korea, democratic peoples republic of': 'north korea',
  'korea, democratic people s republic of': 'north korea',
  'north korea': 'north korea',
  'iran islamic republic of': 'iran',
  'tanzania united republic of': 'tanzania',
  'viet nam': 'vietnam',
  'czech republic': 'czechia',
  czechia: 'czechia',
  turkiye: 'turkey',
  'bolivia (plurinational state of)': 'bolivia',
  'bolivia plurinational state of': 'bolivia',
  'moldova republic of': 'moldova',
  'syrian arab republic': 'syria',
  'venezuela bolivarian republic of': 'venezuela',
  'lao peoples democratic republic': 'laos',
  'democratic republic of the congo': 'democratic republic of the congo',
  'congo, democratic republic of the': 'democratic republic of the congo',
  'congo republic of the': 'congo',
  'hong kong sar': 'hong kong',
  'macau sar': 'macau',
  'taiwan province of china': 'taiwan',
  'palestine state of': 'palestine',
  'myanmar (burma)': 'myanmar',
  'ivory coast': 'cote divoire',
  "cote d'ivoire": 'cote divoire',
  'cote divoire': 'cote divoire',
  'eswatini (swaziland)': 'eswatini',
  'eswatini swaziland': 'eswatini',
};

const normalizeCountry = (name: string) => {
  if (!name) return '';
  const base = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return COUNTRY_CANONICAL[base] || base;
};

const PIE_COLORS = [
  '#2563eb',
  '#7c3aed',
  '#f97316',
  '#10b981',
  '#3361d8',
  '#e11d48',
  '#14b8a6',
  '#a855f7',
  '#fb7185',
  '#facc15',
];

const GaDemographics: React.FC = () => {
  const { ga, isCustomRangePending } = useAnalyticsContext();
  const demographicsError = ga.demographics.error as any;

  const { countriesList, chartData, totalUsers } = useMemo(() => {
    const list = ga.demographics.countries
      .map(country => ({
        country: country.country,
        users: country.users,
        normalized: normalizeCountry(country.country),
      }))
      .sort((a, b) => b.users - a.users)
      .slice(0, 10);

    const totals = new Map<string, number>();

    list.forEach(country => {
      const canonical = country.normalized;
      const current = totals.get(canonical) || 0;
      totals.set(canonical, current + country.users);
    });

    const overall = Array.from(totals.values()).reduce(
      (sum, value) => sum + value,
      0,
    );

    const pieData = Array.from(totals.entries()).map(([key, value], index) => {
      const original = list.find(item => item.normalized === key);
      return {
        name: original?.country || key,
        value,
        color: PIE_COLORS[index % PIE_COLORS.length],
      };
    });

    return {
      countriesList: list.map((item, index) => ({
        ...item,
        color: PIE_COLORS[index % PIE_COLORS.length],
      })),
      chartData: pieData,
      totalUsers: overall,
    };
  }, [ga.demographics.countries]);

  if (!ga.integration.isConfigured) {
    return (
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Audience Snapshot
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">
          {ga.integration.message ||
            'Connect Google Analytics to view audience demographics.'}
        </CardContent>
      </Card>
    );
  }

  if (
    demographicsError &&
    demographicsError.status &&
    demographicsError.status !== 400
  ) {
    return (
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Audience Snapshot
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[220px] items-center justify-center text-sm text-red-600">
          {demographicsError.data?.message ||
            demographicsError.message ||
            'Failed to load demographic data.'}
        </CardContent>
      </Card>
    );
  }

  if (ga.demographics.isLoading) {
    return (
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Audience Snapshot
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="h-4 w-32 animate-pulse rounded bg-muted/50" />
            {Array.from({ length: 4 }).map((_, idx) => (
              <div
                key={`country-skeleton-${idx}`}
                className="h-3 w-full animate-pulse rounded bg-muted/40"
              />
            ))}
          </div>
          <div className="space-y-2">
            <div className="h-4 w-24 animate-pulse rounded bg-muted/50" />
            {Array.from({ length: 3 }).map((_, idx) => (
              <div
                key={`device-skeleton-${idx}`}
                className="h-3 w-full animate-pulse rounded bg-muted/40"
              />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isCustomRangePending) {
    return (
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Audience Snapshot
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">
          Select a start and end date to view Google Analytics insights.
        </CardContent>
      </Card>
    );
  }

  if (!ga.demographics.countries.length) {
    return (
      <Card className="border-none shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold">
            Audience Snapshot
          </CardTitle>
        </CardHeader>
        <CardContent className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">
          No demographic data available for this period.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-none shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold">
          Audience Snapshot
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-6 xl:flex-row">
        <div className="flex-1 overflow-hidden rounded-xl border bg-muted/20 p-4">
          <div className="h-[280px] w-full">
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={70}
                  outerRadius={100}
                  paddingAngle={4}
                  stroke="#0f172a"
                  strokeWidth={1}
                >
                  {chartData.map(entry => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) =>
                    `${Number(value).toLocaleString()} users`
                  }
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-4 text-center text-xs text-muted-foreground">
            Total users from these countries:{' '}
            <span className="font-semibold text-foreground">
              {totalUsers.toLocaleString()}
            </span>
          </p>
        </div>
        <div className="w-full space-y-3 xl:w-72">
          <h4 className="text-sm font-semibold text-foreground">
            Top Countries
          </h4>
          {countriesList.map(country => (
            <div
              key={country.country}
              className="flex items-center justify-between rounded-lg border bg-card/50 px-3 py-2 text-sm"
            >
              <span className="flex items-center gap-2 text-foreground">
                <span
                  className="inline-flex h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: country.color }}
                />
                {country.country}
              </span>
              <span className="text-muted-foreground">
                {country.users.toLocaleString()} users
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default GaDemographics;
